import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Button, Fab, IconButton, Theme, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { addDays, format, parse } from 'date-fns'
import { cs, enUS } from 'date-fns/locale'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Path } from '../../router/BaseRouter/enums'
import { getAvailabilityByDate } from '../../service/instructor-availability.service'
import { getInstructors } from '../../service/instructors.service'
import { getLesson, getLessonsByDate, updateLesson } from '../../service/lessons.service'
import { getProducts } from '../../service/products.service'
import { AvailabilityMap, InstructorDto, LessonDto, ProductDto, Skier } from '../../service/types'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { dayPlanActions, selectedDateSelector } from '../../store/slices/dayPlanSlice'
import { drawerMarginSx } from '../../utils/drawerMargin'
import { getApiErrorMessage } from '../../utils/getApiErrorMessage'
import { DayPlanGrid } from './DayPlanGrid'
import { isRangeAvailable } from './DayPlanGrid/availability'
import { DRAWER_WIDTH, LessonDialog, LessonPreview } from './LessonDialog'

export const filterInstructorsByAvailability = (
  instructors: InstructorDto[],
  availabilityMap: AvailabilityMap,
): InstructorDto[] => {
  return instructors.filter((i) => {
    const hours = availabilityMap[i.id]
    return hours && hours.length > 0
  })
}

export const DayPlan: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const selectedDate = useAppSelector(selectedDateSelector)

  const navigate = useNavigate()
  const { lessonId: urlLessonId } = useParams<{ lessonId?: string }>()

  const [instructors, setInstructors] = useState<InstructorDto[]>([])
  const [lessons, setLessons] = useState<LessonDto[]>([])

  // "New lesson" mode -- local state only (no URL change)
  const [newLessonDialogOpen, setNewLessonDialogOpen] = useState(false)

  const [defaultInstructorId, setDefaultInstructorId] = useState<string>()
  const [defaultTime, setDefaultTime] = useState<string>()
  const [products, setProducts] = useState<ProductDto[]>([])
  const [availabilityMap, setAvailabilityMap] = useState<AvailabilityMap>({})
  const [lessonPreview, setLessonPreview] = useState<LessonPreview>(null)
  const [urlLessonResolving, setUrlLessonResolving] = useState(false)
  const [pendingLessonIds, setPendingLessonIds] = useState<Set<string>>(new Set())
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkProcessed = useRef(false)
  const convertParamsRef = useRef<{
    customerId: string
    skiers?: Skier[]
    requestId?: string
    requestSummary?: string
  } | null>(null)
  const lessonsRef = useRef(lessons)
  lessonsRef.current = lessons

  // Derive effective drawer state from URL (existing lesson) or local state (new lesson)
  const urlLesson = useMemo(
    () => (urlLessonId ? (lessons.find((l) => l.id === urlLessonId) ?? null) : null),
    [urlLessonId, lessons],
  )
  const dialogOpen = urlLessonId
    ? urlLesson !== null || lessons.length === 0 || urlLessonResolving
    : newLessonDialogOpen
  const selectedLesson = urlLesson
  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd')

  // Handle deep-link: ?date=YYYY-MM-DD&lessonId=UUID
  useEffect(() => {
    const dateParam = searchParams.get('date')
    if (dateParam && dateParam !== selectedDate) {
      dispatch(dayPlanActions.setDate(dateParam))
    }
    // eslint-disable-next-line -- run only on mount to sync URL date param (exhaustive-deps)
  }, [])

  // Deep-link: fetch lesson by ID to resolve its date, then switch to that date
  useEffect(() => {
    if (!urlLessonId) return
    let cancelled = false
    setUrlLessonResolving(true)
    void getLesson(urlLessonId)
      .then((res) => {
        if (cancelled) return
        dispatch(dayPlanActions.setDate(res.data.date))
        setUrlLessonResolving(false)
      })
      .catch(() => {
        if (cancelled) return
        setUrlLessonResolving(false)
        navigate(Path.DAY_PLAN)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line -- run only when urlLessonId changes (exhaustive-deps)
  }, [urlLessonId])

  const loadData = useCallback(async () => {
    try {
      const [instructorsRes, lessonsRes, productsRes, availabilityRes] = await Promise.all([
        getInstructors(),
        getLessonsByDate(selectedDate),
        getProducts(),
        getAvailabilityByDate(selectedDate),
      ])
      setInstructors(instructorsRes.data)
      setLessons(lessonsRes.data)
      setProducts(productsRes.data)
      setAvailabilityMap(availabilityRes.data)
    } catch {
      // Error handled silently - toast would be too noisy on date changes
    }
  }, [selectedDate])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const visibleInstructors = useMemo(
    () => filterInstructorsByAvailability(instructors, availabilityMap),
    [instructors, availabilityMap],
  )

  // Legacy deep-link: redirect ?lessonId=UUID to /day-plan/UUID
  useEffect(() => {
    if (deepLinkProcessed.current) return
    const lessonId = searchParams.get('lessonId')
    if (lessonId) {
      deepLinkProcessed.current = true
      const cleaned = new URLSearchParams(searchParams)
      cleaned.delete('lessonId')
      cleaned.delete('date')
      const qs = cleaned.toString()
      navigate(`${Path.DAY_PLAN}/${lessonId}${qs ? `?${qs}` : ''}`, { replace: true })
    }
  }, [searchParams, navigate])

  // Auto-open new lesson dialog from "Convert to Lesson" query params
  useEffect(() => {
    if (deepLinkProcessed.current) return
    const timeParam = searchParams.get('time')
    const customerIdParam = searchParams.get('customerId')
    if (timeParam && customerIdParam) {
      deepLinkProcessed.current = true
      setDefaultInstructorId(undefined)
      setDefaultTime(timeParam)
      setNewLessonDialogOpen(true)
      // Store convert params so LessonDialog can use them
      const requestSummaryStr = searchParams.get('requestSummary') ?? undefined
      let skiers: Skier[] | undefined
      if (requestSummaryStr) {
        try {
          const parsed = JSON.parse(requestSummaryStr)
          skiers = parsed?.skiers
        } catch {
          /* ignore */
        }
      }
      convertParamsRef.current = {
        customerId: customerIdParam,
        skiers,
        requestId: searchParams.get('requestId') ?? undefined,
        requestSummary: requestSummaryStr,
      }
      const cleaned = new URLSearchParams(searchParams)
      cleaned.delete('date')
      cleaned.delete('time')
      cleaned.delete('customerId')
      cleaned.delete('requestId')
      cleaned.delete('requestSummary')
      setSearchParams(cleaned, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleDateChange = (date: Date | null) => {
    if (date) {
      dispatch(dayPlanActions.setDate(format(date, 'yyyy-MM-dd')))
    }
  }

  const handlePrevDay = () => {
    const current = parse(selectedDate, 'yyyy-MM-dd', new Date())
    dispatch(dayPlanActions.setDate(format(addDays(current, -1), 'yyyy-MM-dd')))
  }

  const handleNextDay = () => {
    const current = parse(selectedDate, 'yyyy-MM-dd', new Date())
    dispatch(dayPlanActions.setDate(format(addDays(current, 1), 'yyyy-MM-dd')))
  }

  const handleLessonClick = (lesson: LessonDto) => {
    setDefaultInstructorId(undefined)
    setDefaultTime(undefined)
    navigate(`${Path.DAY_PLAN}/${lesson.id}`)
  }

  const handleEmptyCellClick = (instructorId: string, time: string) => {
    setDefaultInstructorId(instructorId)
    setDefaultTime(time)
    setNewLessonDialogOpen(true)
  }

  const handleAddLesson = () => {
    setDefaultInstructorId(undefined)
    setDefaultTime(undefined)
    setNewLessonDialogOpen(true)
  }

  const handleLessonDrop = useCallback(
    async (lessonId: string, sourceInstructorId: string | null, targetInstructorId: string | null, time: string) => {
      const lesson = lessonsRef.current.find((l) => l.id === lessonId)
      if (!lesson) return

      // Compute new endTime preserving original duration
      const [sh, sm] = lesson.startTime.split(':').map(Number)
      const [eh, em] = lesson.endTime.split(':').map(Number)
      const durationMin = eh * 60 + em - (sh * 60 + sm)
      const [th, tm] = time.split(':').map(Number)
      const endTotal = th * 60 + tm + durationMin
      const newEndTime = `${Math.floor(endTotal / 60)
        .toString()
        .padStart(2, '0')}:${(endTotal % 60).toString().padStart(2, '0')}`

      if (targetInstructorId && !isRangeAvailable(targetInstructorId, time, newEndTime, availabilityMap)) {
        toast.error(t('dayPlan.instructorUnavailable'))
        return
      }

      const timeChanged = time !== lesson.startTime
      const instructorChanged =
        sourceInstructorId !== targetInstructorId &&
        // Don't swap if the target is already assigned to this lesson (e.g. dragging between rows of the same group lesson)
        !(targetInstructorId !== null && lesson.instructorIds.includes(targetInstructorId))

      // Build the new instructorIds array:
      // - Swap the source instructor for the target instructor
      // - If dragged from unassigned: add the target instructor
      // - If dragged to unassigned: remove the source instructor
      let newInstructorIds = [...lesson.instructorIds]
      if (instructorChanged) {
        if (sourceInstructorId === null && targetInstructorId !== null) {
          // From unassigned → add target
          newInstructorIds.push(targetInstructorId)
        } else if (sourceInstructorId !== null && targetInstructorId === null) {
          // To unassigned → remove source
          newInstructorIds = newInstructorIds.filter((id) => id !== sourceInstructorId)
        } else if (sourceInstructorId !== null && targetInstructorId !== null) {
          // Swap source → target
          newInstructorIds = newInstructorIds.map((id) => (id === sourceInstructorId ? targetInstructorId : id))
        }
      }

      // Nothing changed — skip the API call
      if (!timeChanged && !instructorChanged) return

      // Snapshot for rollback on failure
      const snapshot = lessonsRef.current

      // Optimistic update — move lesson immediately in local state
      const optimisticLesson: LessonDto = {
        ...lesson,
        instructorIds: newInstructorIds,
        ...(timeChanged && { startTime: time, endTime: newEndTime }),
      }
      setLessons(snapshot.map((l) => (l.id === lessonId ? optimisticLesson : l)))
      setPendingLessonIds((prev) => new Set([...prev, lessonId]))

      try {
        const update: Record<string, unknown> = { instructorIds: newInstructorIds }
        if (timeChanged) {
          update.startTime = time
          update.endTime = newEndTime
        }
        await updateLesson(lessonId, update as Parameters<typeof updateLesson>[1])
        setPendingLessonIds((prev) => {
          const next = new Set(prev)
          next.delete(lessonId)
          return next
        })
        toast.success(t('general.notification.savedSuccessfully'))
        void loadData()
      } catch (err: unknown) {
        setLessons(snapshot)
        setPendingLessonIds((prev) => {
          const next = new Set(prev)
          next.delete(lessonId)
          return next
        })
        toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
      }
    },
    [t, loadData, availabilityMap],
  )

  const { i18n } = useTranslation()
  const dateLocale = i18n.language === 'cs' ? cs : enUS
  const datePickerValue = parse(selectedDate, 'yyyy-MM-dd', new Date())
  const formattedDay = format(datePickerValue, 'EEEE', { locale: dateLocale })

  return (
    <>
      <ScreenWrapper
        title={t('dayPlan.title')}
        sx={drawerMarginSx(dialogOpen, DRAWER_WIDTH)}
        filter={
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              position: 'fixed',
              right: dialogOpen
                ? Object.fromEntries(Object.entries(DRAWER_WIDTH).map(([bp, w]) => [bp, `${w + 16}px`]))
                : 16,
              top: 80,
              zIndex: 1100,
              bgcolor: 'background.default',
              py: 0.5,
              px: 1,
              transition: (theme: Theme) =>
                theme.transitions.create('right', {
                  easing: dialogOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                  duration: dialogOpen
                    ? theme.transitions.duration.enteringScreen
                    : theme.transitions.duration.leavingScreen,
                }),
            }}
          >
            <Button
              size="large"
              variant="contained"
              onClick={() => dispatch(dayPlanActions.setDate(format(new Date(), 'yyyy-MM-dd')))}
            >
              {t('dayPlan.today')}
            </Button>
            <Button
              size="large"
              variant="outlined"
              onClick={() => dispatch(dayPlanActions.setDate(format(addDays(new Date(), 1), 'yyyy-MM-dd')))}
            >
              {t('dayPlan.tomorrow')}
            </Button>
            <IconButton onClick={handlePrevDay} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 90 }}>
                {formattedDay}
              </Typography>
              <DatePicker
                value={datePickerValue}
                onChange={handleDateChange}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    sx: { width: 160 },
                  },
                }}
              />
            </Box>
            <IconButton onClick={handleNextDay} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        }
      >
        <DayPlanGrid
          isToday={isToday}
          instructors={visibleInstructors}
          lessons={lessons}
          availabilityMap={availabilityMap}
          selectedLessonId={urlLessonId}
          onLessonClick={handleLessonClick}
          onEmptyCellClick={handleEmptyCellClick}
          onLessonDrop={handleLessonDrop}
          lessonPreview={lessonPreview}
          pendingLessonIds={pendingLessonIds}
        />

        <Fab
          color="primary"
          onClick={handleAddLesson}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: dialogOpen ? Object.fromEntries(Object.entries(DRAWER_WIDTH).map(([bp, w]) => [bp, w + 24])) : 24,
            transition: (theme: Theme) =>
              theme.transitions.create('right', {
                easing: dialogOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                duration: dialogOpen
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
          }}
        >
          <AddIcon />
        </Fab>
      </ScreenWrapper>

      <LessonDialog
        open={dialogOpen}
        onClose={() => {
          if (urlLessonId) {
            navigate(Path.DAY_PLAN)
          } else {
            setNewLessonDialogOpen(false)
          }
          setLessonPreview(null)
          convertParamsRef.current = null
        }}
        onSaved={() => void loadData()}
        lesson={selectedLesson}
        defaultInstructorId={defaultInstructorId}
        defaultTime={defaultTime}
        defaultDate={selectedDate}
        defaultCustomerId={convertParamsRef.current?.customerId}
        defaultSkiers={convertParamsRef.current?.skiers}
        requestId={convertParamsRef.current?.requestId}
        requestSummary={convertParamsRef.current?.requestSummary}
        instructors={visibleInstructors}
        products={products}
        availabilityMap={availabilityMap}
        onPreviewChange={setLessonPreview}
      />
    </>
  )
}
